import { inject, Pipe, PipeTransform, SecurityContext } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';

@Pipe({
  name: 'formatDate'
})
export class FormatDatePipe implements PipeTransform {
  private readonly sanitizer = inject(DomSanitizer);
  constructor() { }

  transform(value: string, customClass?: string): SafeHtml {
    if (!value) return '';

    // Strip any attacker-controlled markup from the source text before adding our own
    // <strong>/<span> highlighting and trusting the result for [innerHTML].
    value = this.sanitizer.sanitize(SecurityContext.HTML, value) ?? '';

    // Common date patterns to match
    const datePatterns = [
      // MM/DD/YYYY or MM/DD/YY
      /\b\d{1,2}\/\d{1,2}\/\d{2,4}\b/g,
      // MM-DD-YYYY or MM-DD-YY
      /\b\d{1,2}-\d{1,2}-\d{2,4}\b/g,
      // YYYY-MM-DD
      /\b\d{4}-\d{1,2}-\d{1,2}\b/g,
      // Month DD, YYYY (with optional ordinal suffixes)
      /\b(?:Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)[a-z]* \d{1,2}(?:st|nd|rd|th)?,? \d{4}\b/gi,
      // DD Month YYYY (with optional ordinal suffixes)
      /\b\d{1,2}(?:st|nd|rd|th)? (?:Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)[a-z]* \d{4}\b/gi,
      // MM/DD/YYYY HH:MM AM/PM
      /\b\d{1,2}\/\d{1,2}\/\d{2,4} \d{1,2}:\d{2} (?:AM|PM)\b/gi,
      // MM-DD-YYYY HH:MM AM/PM
      /\b\d{1,2}-\d{1,2}-\d{2,4} \d{1,2}:\d{2} (?:AM|PM)\b/gi
    ];

    let result = value;

    //default to bold
    datePatterns.forEach(pattern => {
      result = result.replace(pattern, (match) => {
        if (customClass) {
          return `<span class="${customClass}">${match}</span>`;
        } else {
          return `<strong>${match}</strong>`;
        }
      });
    });

    return this.sanitizer.bypassSecurityTrustHtml(result);
  }

  static toDate(dateInput?: string | Date | null): Date | null {
    if (!dateInput) return null;
    if (dateInput instanceof Date) return dateInput;

    // Parse date string (YYYY-MM-DD format) as local time to avoid timezone shifts
    if (typeof dateInput === 'string') {
      const parts = dateInput.split('T')[0].split('-');
      if (parts.length === 3) {
        const [year, month, day] = parts.map(p => Number.parseInt(p, 10));
        const d = new Date(year, month - 1, day);
        return Number.isNaN(d.getTime()) ? null : d;
      }
    }

    const d = new Date(dateInput);
    return Number.isNaN(d.getTime()) ? null : d;
  }

  static formatDateToString(dateInput?: string | Date | null): string {
    if (dateInput instanceof Date) {
      const month = String(dateInput.getMonth() + 1).padStart(2, '0');
      const day = String(dateInput.getDate()).padStart(2, '0');
      return `${dateInput.getFullYear()}-${month}-${day}`;
    }
    return typeof dateInput === 'string' ? dateInput : '';
  }

  static isTodayWithin(start?: string | Date | null, end?: string | Date | null, today: Date = new Date()): boolean {
    const startDate = FormatDatePipe.toDate(start);
    const endDate = FormatDatePipe.toDate(end);
    const todayNumber = FormatDatePipe.dayNumber(today);
    return (
      (!startDate || FormatDatePipe.dayNumber(startDate) <= todayNumber) &&
      (!endDate || todayNumber <= FormatDatePipe.dayNumber(endDate))
    );
  }

  static isBeforeToday(value?: string | Date | null, today: Date = new Date()): boolean {
    const date = FormatDatePipe.toDate(value);
    return !!date && FormatDatePipe.dayNumber(date) < FormatDatePipe.dayNumber(today);
  }

  private static dayNumber(date: Date): number {
    return date.getFullYear() * 10000 + (date.getMonth() + 1) * 100 + date.getDate();
  }
} 